import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import DashboardLayout from "../../components/DashboardLayout";
import DealSubmissionModal from "../../components/DealSubmissionModal";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { FileText, Pencil, Trash2, Send, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const AgentDraftsPage = () => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const perPage = 10;

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchDrafts = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/deals/drafts`, {
        withCredentials: true, headers, params: { page, per_page: perPage },
      });
      const data = res.data;
      setDrafts(data.drafts || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      console.error("Failed to fetch drafts:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrafts();
  }, [page]);

  const handleEditDraft = (draft) => {
    setEditingDraft(draft);
    setShowModal(true);
  };

  const handleNewDraft = () => {
    setEditingDraft(null);
    setShowModal(true);
  };

  const handleDeleteDraft = async (draftId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/deals/drafts/${draftId}`, { withCredentials: true, headers });
      toast.success("Draft deleted");
      fetchDrafts();
    } catch (error) {
      toast.error("Failed to delete draft");
    }
  };

  const handleSubmitDraft = async (draftId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/deals/drafts/${draftId}/submit`, {}, { withCredentials: true, headers });
      toast.success("Deal submitted for review!");
      fetchDrafts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Cannot submit — please complete all required fields first");
    }
  };

  const formatCurrency = (value) => {
    if (!value) return "";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  };

  const getPropertyTypeLabel = (type) => {
    const labels = { single_family: "Single Family", multi_family: "Multi-Family", apartment: "Apartment", commercial: "Commercial", land: "Land", mixed_use: "Mixed Use" };
    return labels[type] || type || "";
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <DashboardLayout title="My Drafts">
      <div className="space-y-6 animate-fade-in" data-testid="agent-drafts-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">My Drafts</h1>
            <p className="text-muted-foreground mt-1">
              {total} draft{total !== 1 ? "s" : ""} saved
            </p>
          </div>
          <Button className="bg-blue-500 hover:bg-blue-600" onClick={handleNewDraft} data-testid="new-draft-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Deal
          </Button>
        </div>

        {/* Drafts list */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-5">
                  <div className="h-5 bg-muted rounded w-2/3 mb-2" />
                  <div className="h-4 bg-muted rounded w-1/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : drafts.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-lg font-medium text-muted-foreground">No drafts yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Start a new deal and save it as a draft anytime
              </p>
              <Button className="mt-4 bg-blue-500 hover:bg-blue-600" onClick={handleNewDraft}>
                <Plus className="w-4 h-4 mr-2" /> New Deal
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {drafts.map((draft) => (
              <Card
                key={draft.deal_id}
                className="hover:shadow-md transition-shadow border-dashed"
                data-testid={`draft-card-${draft.deal_id}`}
              >
                <CardContent className="p-5 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleEditDraft(draft)}>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-foreground truncate">
                        {draft.property_address || "Untitled Draft"}
                      </h3>
                      <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-200 shrink-0">
                        Draft
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {[draft.city, draft.state, draft.zip_code].filter(Boolean).join(", ") || "No location set"}
                      {draft.property_type ? ` · ${getPropertyTypeLabel(draft.property_type)}` : ""}
                      {draft.asking_price ? ` · ${formatCurrency(draft.asking_price)}` : ""}
                    </p>
                    <p className="text-xs text-muted-foreground/60 mt-1">
                      Saved {formatTimeAgo(draft.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => handleEditDraft(draft)} data-testid={`draft-edit-${draft.deal_id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={() => handleSubmitDraft(draft.deal_id)}
                      data-testid={`draft-submit-${draft.deal_id}`}
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleDeleteDraft(draft.deal_id)}
                      data-testid={`draft-delete-${draft.deal_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 pt-4" data-testid="drafts-pagination">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>

      <DealSubmissionModal
        open={showModal}
        onOpenChange={(open) => {
          setShowModal(open);
          if (!open) setEditingDraft(null);
        }}
        onSuccess={fetchDrafts}
        draftData={editingDraft}
      />
    </DashboardLayout>
  );
};

export default AgentDraftsPage;
